import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import React from 'react';

import type { Application } from '@src/@types/app';
import { PageHead } from '@src/components/PageHead';
import { getPageLandingData } from '@src/config/getPageLandingData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { PageContext } from '@src/config/PageContext';
import { MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { useLandingPage } from '@src/hooks/useLandingPage';
import { useHasUtmLabel } from '@src/reducers/route';
import { Layout as AppLayout } from 'app/Layout';
import Disclaimer from 'entities/Seo/ui/Disclaimer';
import ProductList from 'features/ProductList/ui/ProductList/ProductList';
import { Container } from 'shared/ui/Container/Container';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { LightweightCard } from 'widgets/LightweightCard';

import styles from './LandingPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.LANDING,
  source: 'search',
  withMonetizationCardLogoClick: true,
  withCardProductLink: true,
  withCardDetailsClick: true,
};

export const LandingPage: NextPage = () => {
  const { filter } = useLandingPage(ListKeys.LANDING, MODES.WL_PAGE_WITH_CHANNEL);
  const router = useRouter();
  const hasUtmLabel = useHasUtmLabel();
  const [pathname] = router.asPath.split('?');

  return (
    <AppLayout mode="landing">
      {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
      <PageContext.Provider value={pageConfig}>
        <PageHead />
        <BrandingHeaders containerClassName={styles.page_header} afterHeaders={null} />
        <Container>
          <ProductList
            pathname={pathname}
            filter={filter}
            renderCard={(props) => (
              <LightweightCard
                {...props}
                hasUtmLabel={hasUtmLabel}
                contentSettings={{
                  ...props.contentSettings,
                  term: { visible: false },
                  psk: { visible: false },
                }}
                meta={{ ...props.meta, withCardDetailsClick: false }}
              />
            )}
          />
          <Disclaimer />
        </Container>
      </PageContext.Provider>
    </AppLayout>
  );
};

LandingPage.getInitialProps = (context: Application.ReduxNextPageContext) =>
  getPageLandingData(context, ListKeys.LANDING, MODES.WL_PAGE_WITH_CHANNEL);
